import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { CMP_READERS, formatOwnConsent, ICmpConsent, OWN_CONSENT_COOKIE, parseCookies, readOwnConsent } from './consent-cmp-readers';
import { DebugService } from './debug.service';

/** 'assumed' = asking is turned off in settings, consent is given by other means (e.g. the customer's own script) */
export type ConsentSource = 'cmp' | 'own' | 'assumed' | 'none';

export interface IConsentState {
  /** Visitor has made a choice, either in the customer's CMP or in our own banner */
  decided: boolean;
  analytics: boolean;
  marketing: boolean;
  source: ConsentSource;
}

const UNDECIDED: IConsentState = { decided: false, analytics: false, marketing: false, source: 'none' };
const OWN_CONSENT_DAYS = 180;
const SECONDS_IN_DAY = 24 * 60 * 60;

/**
 * Decides whether analytics (GA4) and marketing (Meta Pixel) may be used.
 *
 * 1. The consent cookie of a known CMP on the customer's main domain is used if it is visible here.
 * 2. Otherwise our own banner asks, and the choice is stored in the cookie hlx_consent.
 * Nothing is allowed until a choice exists.
 */
@Injectable({
  providedIn: 'root'
})
export class ConsentService {

  private readonly stateSubject = new BehaviorSubject<IConsentState>(UNDECIDED);
  private readonly bannerOpenSubject = new BehaviorSubject<boolean>(false);
  private required = false;

  readonly state$ = this.stateSubject.asObservable();
  readonly bannerOpen$ = this.bannerOpenSubject.asObservable();

  constructor(private debug: DebugService) { }

  get state(): IConsentState {
    return this.stateSubject.value;
  }

  /** Own banner can be reopened to change the choice. Not offered when the choice comes from the customer's CMP. */
  get canChangeChoice(): boolean {
    return this.required && this.state.source === 'own';
  }

  /**
   * @param required true if something that needs consent (GA4, Meta Pixel) is configured.
   * If nothing is configured, no banner is shown.
   * @param askForConsent false if consent is given by other means. Then no banner or settings button is shown
   * and analytics and marketing are allowed.
   */
  init(required: boolean, askForConsent = true) {
    this.required = required;
    if (!required) {
      return;
    }

    if (!askForConsent) {
      this.stateSubject.next({ decided: true, analytics: true, marketing: true, source: 'assumed' });
      this.bannerOpenSubject.next(false);
      return;
    }

    const state = this.readStoredState();
    this.stateSubject.next(state);
    this.bannerOpenSubject.next(!state.decided);
  }

  acceptAll() {
    this.saveOwnChoice({ analytics: true, marketing: true });
  }

  rejectAll() {
    this.saveOwnChoice({ analytics: false, marketing: false });
  }

  openBanner() {
    this.bannerOpenSubject.next(true);
  }

  private readStoredState(): IConsentState {
    const cookies = parseCookies(document.cookie);

    for (const cmp of CMP_READERS) {
      const consent = cmp.read(cookies);
      if (consent) {
        this.debug.log('ConsentService', 'consent read from', cmp.name, consent);
        return this.toState(consent, 'cmp');
      }
    }

    const own = readOwnConsent(cookies);
    return own ? this.toState(own, 'own') : UNDECIDED;
  }

  private saveOwnChoice(consent: ICmpConsent) {
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${OWN_CONSENT_COOKIE}=${encodeURIComponent(formatOwnConsent(consent))}; path=/; max-age=${OWN_CONSENT_DAYS * SECONDS_IN_DAY}; SameSite=Lax${secure}`;

    this.stateSubject.next(this.toState(consent, 'own'));
    this.bannerOpenSubject.next(false);
  }

  private toState(consent: ICmpConsent, source: ConsentSource): IConsentState {
    return { decided: true, analytics: consent.analytics, marketing: consent.marketing, source };
  }
}
